'use client'

import { m, useScroll, useTransform } from 'framer-motion'
import {
  AlertCircle,
  ChevronRight,
  Clock,
  Copyright,
  FileText,
  Gavel,
  Mail,
  Scale,
  Shield,
} from 'lucide-react'
import Link from 'next/link'
import React, { useEffect, useState } from 'react'

const sections = [
  {
    id: 'general',
    title: '1. Предмет соглашения',
    icon: FileText,
    color: 'text-blue-400',
    content: (
      <>
        <p className="mb-6 text-xl leading-relaxed text-gray-300">
          1.1. Настоящее Пользовательское соглашение (далее — Соглашение) регулирует отношения между
          Администрацией сайта Цифровой горизонт (ИП Носиков Олег Николаевич) и Пользователем
          данного сайта.
        </p>
        <p className="text-xl leading-relaxed text-gray-300">
          1.2. Использование сайта, включая просмотр страниц, отправку форм и заказ услуг, означает
          полное и безоговорочное согласие Пользователя с настоящим Соглашением.
        </p>
      </>
    ),
  },
  {
    id: 'rights',
    title: '2. Права и обязанности',
    icon: Scale,
    color: 'text-purple-400',
    content: (
      <>
        <p className="mb-8 text-xl leading-relaxed text-gray-300">
          2.1. Пользователь обязуется использовать сайт только в законных целях и не предпринимать
          действий, которые могут нарушить работу сайта.
        </p>
        <div className="mb-8 space-y-4">
          <h4 className="mb-4 text-xl font-bold text-white">Пользователю запрещено:</h4>
          {[
            'Использовать скрипты для автоматического сбора данных (парсинг)',
            'Пытаться получить доступ к закрытым разделам сайта',
            'Распространять вредоносное ПО через формы обратной связи',
            'Копировать дизайн и контент сайта без разрешения',
          ].map((item, i) => (
            <div
              key={i}
              className="flex items-center gap-4 rounded-2xl border border-white/5 bg-white/[0.03] p-5 transition-colors hover:bg-white/[0.06]"
            >
              <AlertCircle className="h-6 w-6 flex-shrink-0 text-red-400" />
              <span className="text-lg font-medium text-white">{item}</span>
            </div>
          ))}
        </div>
      </>
    ),
  },
  {
    id: 'intellectual',
    title: '3. Интеллектуальная собственность',
    icon: Copyright,
    color: 'text-cyan-400',
    content: (
      <>
        <p className="mb-6 text-xl leading-relaxed text-gray-300">
          3.1. Весь контент, размещенный на сайте (тексты, изображения, логотипы, дизайн-код,
          анимации), является исключительной собственностью Администрации сайта или используется на
          законных основаниях.
        </p>
        <p className="text-xl leading-relaxed text-gray-300">
          3.2. Любое использование материалов сайта без письменного разрешения Администрации
          запрещено и влечет за собой ответственность в соответствии с законодательством РФ.
        </p>
      </>
    ),
  },
  {
    id: 'liability',
    title: '4. Ответственность сторон',
    icon: Gavel,
    color: 'text-emerald-400',
    content: (
      <>
        <p className="mb-6 text-xl leading-relaxed text-gray-300">
          4.1. Администрация не несет ответственности за любые убытки, возникшие вследствие
          использования или невозможности использования сайта.
        </p>
        <p className="text-xl leading-relaxed text-gray-300">
          4.2. Администрация оставляет за собой право в любой момент изменять содержание сайта,
          обновлять услуги и цены без предварительного уведомления.
        </p>
      </>
    ),
  },
  {
    id: 'dispute',
    title: '5. Разрешение споров',
    icon: Shield,
    color: 'text-pink-400',
    content: (
      <p className="text-xl leading-relaxed text-gray-300">
        Все споры и разногласия, возникающие в связи с исполнением настоящего Соглашения, решаются
        путем переговоров. В случае невозможности прийти к соглашению, спор подлежит рассмотрению в
        судебном порядке по месту нахождения Администрации сайта.
      </p>
    ),
  },
]

export default function TermsPage() {
  const lastUpdate = '09.02.2024'
  const [activeSection, setActiveSection] = useState('general')

  // Scroll progress for visuals
  const { scrollYProgress } = useScroll()
  const scaleX = useTransform(scrollYProgress, [0, 1], [0, 1])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id)
          }
        })
      },
      { threshold: 0.5 },
    )

    sections.forEach((s) => {
      const el = document.getElementById(s.id)
      if (el) observer.observe(el)
    })

    return () => observer.disconnect()
  }, [])

  return (
    <main className="relative min-h-screen bg-[#020617] px-4 pt-8 pb-32 md:pt-32">
      {/* Scroll Progress Bar */}
      <m.div
        style={{ scaleX }}
        className="fixed top-0 right-0 left-0 z-50 h-1 origin-left bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"
      />

      {/* Background Gradient */}
      <div className="pointer-events-none fixed inset-0 bg-gradient-to-b from-blue-900/10 via-purple-900/5 to-transparent" />

      <div className="relative z-10 mx-auto w-[90vw] max-w-[1600px]">
        {/* Header */}
        <m.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-24 flex flex-col items-center text-center"
        >
          <div className="mb-8 inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-2.5 backdrop-blur-sm">
            <Gavel className="h-4 w-4 text-blue-400" />
            <span className="text-sm font-medium tracking-wider text-gray-300 uppercase">
              Официальный документ
            </span>
          </div>

          <h1 className="mb-8 text-5xl leading-[1.1] font-bold tracking-tight text-white md:text-7xl lg:text-8xl">
            Пользовательское
            <br />
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              Соглашение
            </span>
          </h1>

          <div className="flex flex-wrap items-center justify-center gap-6 rounded-2xl border border-white/5 bg-white/[0.03] px-8 py-3 text-lg text-gray-400 backdrop-blur-sm">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-gray-500" />
              <span>
                Обновлено: <strong className="font-medium text-white">{lastUpdate}</strong>
              </span>
            </div>
            <div className="hidden h-1 w-1 rounded-full bg-gray-700 sm:block" />
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-500" />
              <span>
                Версия: <strong className="font-medium text-white">1.0</strong>
              </span>
            </div>
          </div>
        </m.div>

        <div className="relative grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-24">
          {/* Left Sidebar - Sticky */}
          <div className="relative lg:col-span-4">
            <div className="sticky top-32 space-y-12">
              {/* Navigation */}
              <nav className="hidden space-y-2 lg:block">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className={`group flex items-center gap-4 rounded-xl p-4 transition-all duration-300 ${activeSection === section.id ? 'border border-white/10 bg-white/5 shadow-lg' : 'border border-transparent hover:bg-white/[0.02]'}`}
                    onClick={(e) => {
                      e.preventDefault()
                      document
                        .getElementById(section.id)
                        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                    }}
                  >
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-lg transition-colors ${activeSection === section.id ? 'bg-white/10' : 'bg-white/5 group-hover:bg-white/10'}`}
                    >
                      <section.icon
                        className={`h-5 w-5 ${activeSection === section.id ? section.color : 'text-gray-500 group-hover:text-gray-300'}`}
                      />
                    </div>
                    <span
                      className={`text-lg font-medium ${activeSection === section.id ? 'text-white' : 'text-gray-500 group-hover:text-gray-300'}`}
                    >
                      {section.title}
                    </span>
                    {activeSection === section.id && (
                      <ChevronRight className="ml-auto h-5 w-5 text-white opacity-50" />
                    )}
                  </a>
                ))}
              </nav>

              {/* Legal Entity Card */}
              <div className="group relative overflow-hidden rounded-[32px] border border-white/10 bg-gradient-to-br from-[#0c1220] to-[#05080f] p-8 shadow-2xl">
                <div className="absolute inset-0 bg-blue-500/5 opacity-0 blur-xl transition-opacity duration-700 group-hover:opacity-100" />
                <div className="relative z-10">
                  <h3 className="mb-6 flex items-center gap-3 text-xl font-bold text-white">
                    <Scale className="h-6 w-6 text-blue-400" />
                    Администрация
                  </h3>
                  <div className="space-y-6">
                    <div>
                      <div className="mb-1 text-sm tracking-wider text-gray-500 uppercase">
                        Владелец сайта
                      </div>
                      <div className="text-lg font-medium text-white">
                        ИП Носиков Олег Николаевич
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="mb-1 text-sm tracking-wider text-gray-500 uppercase">
                          ИНН
                        </div>
                        <div className="inline-block rounded-lg bg-white/5 px-2 py-1 font-mono text-lg text-white">
                          010519387677
                        </div>
                      </div>
                      <div>
                        <div className="mb-1 text-sm tracking-wider text-gray-500 uppercase">
                          ОГРНИП
                        </div>
                        <div className="inline-block rounded-lg bg-white/5 px-2 py-1 font-mono text-lg text-white">
                          324010000005261
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-8 border-t border-white/5 pt-8">
                    <a
                      href="mailto:nosikov2014@list.ru"
                      className="flex items-center gap-3 text-gray-400 transition-colors hover:text-white"
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded-full border border-white/5 bg-white/5 text-blue-400">
                        <Mail className="h-5 w-5" />
                      </div>
                      <span className="text-lg">nosikov2014@list.ru</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content - Sections */}
          <div className="space-y-12 lg:col-span-8">
            {sections.map((section, idx) => (
              <section key={section.id} id={section.id} className="group scroll-mt-32">
                <m.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-100px' }}
                  transition={{ duration: 0.6, delay: idx * 0.1 }}
                  className="relative overflow-hidden rounded-[40px] border border-white/5 bg-[#0c1220]/60 p-8 backdrop-blur-md transition-colors hover:border-white/10 md:p-12 lg:p-16"
                >
                  <div
                    className={`pointer-events-none absolute top-0 right-0 h-64 w-64 rounded-full bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 blur-[80px] transition-opacity duration-700 group-hover:opacity-100`}
                  />

                  <div className="relative z-10">
                    <div className="mb-8 flex items-center gap-4">
                      <h2 className="text-3xl font-bold text-white md:text-5xl">{section.title}</h2>
                    </div>

                    <div className="prose prose-invert max-w-none">{section.content}</div>
                  </div>
                </m.div>
              </section>
            ))}

            <div className="mt-24 border-t border-white/5 pt-12 text-center">
              <p className="mb-8 text-xl text-gray-500">Возникли юридические вопросы?</p>
              <Link
                href="/"
                className="inline-flex items-center gap-2 border-b border-white/20 pb-1 text-lg text-white transition-all hover:border-white hover:text-blue-400"
              >
                Вернуться на главную
              </Link>
            </div>
          </div>
        </div>
      </div>
    </main>
  )
}
